import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PlusSquare, BarChart3, Activity, User as UserIcon,
  Truck, History as HistoryIcon, LogOut,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { colors, spacing, fonts, SERIF } from "@/theme";

const MANAGER_NAV = [
  { to: "/board", label: "Board", icon: LayoutDashboard, testId: "nav-board" },
  { to: "/create", label: "New move", icon: PlusSquare, testId: "nav-create" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
  { to: "/analytics", label: "Analytics", icon: Activity, testId: "nav-analytics" },
  { to: "/profile", label: "Profile", icon: UserIcon, testId: "nav-profile" },
];

const DRIVER_NAV = [
  { to: "/pending", label: "Pending", icon: Truck, testId: "nav-pending" },
  { to: "/history", label: "History", icon: HistoryIcon, testId: "nav-history" },
  { to: "/profile", label: "Profile", icon: UserIcon, testId: "nav-profile" },
];

export function AppLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === "driver" ? DRIVER_NAV : MANAGER_NAV;
  const roleLabel = user?.role === "manager" ? "Manager" : user?.role === "plant" ? "Plant User" : "Driver";

  const doSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div style={s.shell}>
      <aside style={s.sidebar}>
        <div style={s.brandBox}>
          <img
            src="https://customer-assets.emergentagent.com/job_yard-logistics-hub-1/artifacts/mue4tej5_GSGROUP-LOGO-RGB_MAIN.jpg"
            alt="GS Group"
            style={s.logo}
          />
          <div style={s.brandTag}>GS Group – Yard Management</div>
        </div>

        <nav style={s.nav}>
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                style={({ isActive }) => ({ ...s.navItem, ...(isActive ? s.navItemActive : {}) })}
              >
                {({ isActive }) => (
                  <>
                    <Icon size={18} color={isActive ? colors.onBrandPrimary : colors.onSurfaceTertiary} />
                    <span style={{ ...s.navLabel, color: isActive ? colors.onBrandPrimary : colors.onSurface }}>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div style={s.userBox}>
          <div style={s.userName}>{user?.name}</div>
          <div style={s.userRole}>{roleLabel}</div>
          <button data-testid="signout-button" style={s.signOut} onClick={doSignOut}>
            <LogOut size={16} color={colors.error} />
            <span style={s.signOutText}>Sign out</span>
          </button>
        </div>
      </aside>

      <main style={s.main}>
        <Outlet />
      </main>
    </div>
  );
}

const s = {
  shell: { display: "flex", minHeight: "100vh", backgroundColor: colors.surface, fontFamily: SERIF },
  sidebar: {
    width: 260, flexShrink: 0, backgroundColor: colors.surfaceSecondary,
    borderRight: `2px solid ${colors.onSurface}`, display: "flex", flexDirection: "column",
    position: "sticky", top: 0, height: "100vh",
  },
  brandBox: {
    padding: spacing.lg, borderBottom: `2px solid ${colors.onSurface}`,
    display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.sm,
  },
  logo: { width: 150, height: 90, objectFit: "contain" },
  brandTag: { ...fonts.monoBold, fontSize: 11, color: colors.accent, textAlign: "center", letterSpacing: 0 },
  nav: { display: "flex", flexDirection: "column", padding: spacing.md, gap: spacing.sm, flex: 1 },
  navItem: {
    display: "flex", alignItems: "center", gap: spacing.md, padding: `${spacing.md}px ${spacing.md}px`,
    borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, textDecoration: "none",
    cursor: "pointer",
  },
  navItemActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  navLabel: { ...fonts.monoBold, fontSize: 13, letterSpacing: 0 },
  userBox: { padding: spacing.lg, borderTop: `2px solid ${colors.onSurface}` },
  userName: { ...fonts.display, fontSize: 15, color: colors.onSurface },
  userRole: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: 2, marginBottom: spacing.md },
  signOut: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, width: "100%",
    borderWidth: 2, borderStyle: "solid", borderColor: colors.error, backgroundColor: "transparent", padding: spacing.sm, cursor: "pointer",
  },
  signOutText: { ...fonts.monoBold, fontSize: 12, color: colors.error, letterSpacing: 0 },
  main: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", maxHeight: "100vh", overflowY: "auto" },
};
